import React, { useState, useCallback, ChangeEvent, useMemo } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import { useDebouncedEffect } from '~/App/shared/hooks/use-debounced-effect';
import { useBitnetSearchContext } from './context/BitNetSearchContext';

import Text from '~/App/shared/components/Fields/Text';
import { IDeceasedContact } from '~/types/IDeceasedContact';
import { NewPersonModal } from './components/NewPersonModal';
import { Preview } from './components/Preview';
import { DropdownMenu } from './components/Dropdown';
import { IValidation } from '~/App/shared/components/Validation';

const Wrapper = styled.div`
  margin-top: 0.75rem;
`;

interface Props {
  validation: IValidation;
}

export const BitNetSearch = (props: Props) => {
  const { validation } = props;
  const {
    search,
    resetMonths,
    months,
    value,
    onSelect,
    isModalOpen,
    closeModal,
    openModal,
    clearValue
  } = useBitnetSearchContext() ?? {};
  const dropdownId = uuid();

  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const shouldSearch = useMemo(() => {
    if (!inputValue) {
      return false;
    }

    return inputValue.length > 2;
  }, [inputValue]);

  useDebouncedEffect(
    () => {
      if (!shouldSearch) {
        setShowDropdown(false);
        return;
      }

      setShowDropdown(true);
      search(inputValue);
    },
    300,
    [inputValue, months, shouldSearch]
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      setInputValue(value);
      resetMonths();
    },
    [resetMonths]
  );

  const handleOnSelect = (newValue: IDeceasedContact | null) =>
    onSelect(newValue, (selected) => {
      if (selected) {
        setInputValue('');
      }
    });

  return (
    <Wrapper>
      <DropdownMenu
        id={dropdownId}
        onSelect={handleOnSelect}
        show={showDropdown}
        close={() => setShowDropdown(false)}
      >
        {(inputRef) => (
          <Text
            inputRef={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Den avlidnes namn"
            suffix="search"
            role="combobox"
            aria-haspopup="listbox"
            aria-controls={dropdownId}
            aria-owns={dropdownId}
            aria-expanded={showDropdown}
            errors={
              validation.errors['productOptions.inMemoryOfContact.firstName']
            }
            isInvalid={validation.isInvalid(
              'productOptions.inMemoryOfContact.firstName'
            )}
            onBlur={() => {
              if (!showDropdown) {
                validation.showError(
                  'productOptions.inMemoryOfContact.firstName'
                );
              }
            }}
          />
        )}
      </DropdownMenu>
      <Preview openModal={openModal} value={value} clear={clearValue} />
      {isModalOpen && (
        <NewPersonModal
          value={value}
          onSubmit={handleOnSelect}
          closeModal={closeModal}
        />
      )}
    </Wrapper>
  );
};
